<template>
  <v-combobox ref="recipientInput" v-model="selectedRecipients" multiple :items="allParticipants" :label="label"
    :tabindex="tabindex" no-data-text="Nothing match your search" append-icon item-value="index"
    :item-text="getItemText" small-chips hide-selected :return-object="true" dense class="no-margin-bottom custom-label with-btns"
    :search-input.sync="searchPrefix" @update:search-input="emitSearchPrefixUpdated" :error="hasValidationError"
    :error-messages="validationMessages">

    <template slot="item" slot-scope="data">
      <v-list-tile-content>
        <v-list-tile-title>{{ formatRecipientEmail(data.item) }}</v-list-tile-title>
      </v-list-tile-content>
    </template>

    <template slot="selection" slot-scope="data">
      <v-chip @input="removeChip(data.item)" close>{{ recipientChipLabel(data.item) }}</v-chip>
    </template>

    <template slot="append-outer">
      <div class="cc-bcc-buttons" v-if="displayCCbuttons">
        <span class="cc-bcc" @click="emitCCToggled">Cc</span>
        <span class="cc-bcc ml-2" @click="emitBCCToggled">Bcc</span>
      </div>
      <div v-else>&nbsp;</div>
    </template>

    <template v-slot:no-data>
      <v-list-tile>
        <v-list-tile-content>
          <v-list-tile-title>
            No results matching "<strong>{{ searchPrefix }}</strong>". Press <kbd>enter</kbd> to add recipient.
          </v-list-tile-title>
        </v-list-tile-content>
      </v-list-tile>
    </template>

  </v-combobox>
</template>

<script>
import EmailAddress from "@/lib/EmailAddress.js"

export default {
  name: "EmailAddressList",
  components: {},
  data: () => ({
    searchPrefix: "",
    selectedRecipients: [],
    validationError: "",
    isResettingRecipients: false
  }),
  methods: {
    removeChip: function (chip) {
      this.$emit("removeRecipient", chip)
    },
    recipientChipLabel: function (recipient) {
      return this.formatRecipientEmail(recipient)
    },
    getItemText(item) {
      // Return searchable text for v-combobox filtering
      // This is used for client-side filtering, not display
      if (!item) return ""
      if (typeof item === "string") return item
      // Skip header items (they have a 'header' property)
      if (item.header) return item.header
      // Return both displayName and email for better search matching
      const displayName = item.displayName || ""
      const email = item.email || ""
      return `${displayName} ${email}`.trim()
    },
    formatRecipientEmail(recipient) {
      if (!recipient) {
        return ""
      }
      // Handle string recipients (custom email addresses)
      if (typeof recipient === "string") {
        return recipient
      }

      const email = recipient.email || ""
      const displayName = recipient.displayName || ""

      // Use EmailAddress class for RFC 2822 compliant formatting
      if (!email) {
        // If no email, return in standard format with "no-email-available"
        if (displayName) {
          const emailAddr = new EmailAddress(displayName, "no-email-available")
          return emailAddr.formattedEmail
        }
        return "no-email-available"
      }

      // Use EmailAddress class to format in RFC 2822 format
      const emailAddr = new EmailAddress(displayName, email)
      return emailAddr.formattedEmail
    },
    emitCCToggled: function () {
      this.$emit("ccToggled")
    },
    emitBCCToggled: function () {
      this.$emit("bccToggled")
    },
    emitSearchPrefixUpdated: function () {
      this.$emit("update-search-prefix", this.searchPrefix)
    },
    isCustomRecipient(recipient) {
      const isCustom = typeof recipient === "string"
      return isCustom
    },
    isValidEmail(value = "") {
      // Use EmailAddress class for RFC 2822 compliant validation
      const isValid = EmailAddress.isValidEmail(value)
      return isValid
    },
    findInvalidRecipient(additions = []) {
      const invalidRecipient = additions.find(recipient => {
        const isCustom = this.isCustomRecipient(recipient)
        const isValid = this.isValidEmail(recipient)
        return isCustom && !isValid
      })
      return invalidRecipient
    },
    focusInput() {
      const combo = this.$refs.recipientInput
      if (!combo) {
        return
      }

      if (typeof combo.focus === "function") {
        combo.focus()
        return
      }

      if (combo.$refs && combo.$refs.input && typeof combo.$refs.input.focus === "function") {
        combo.$refs.input.focus()
      }
    },
    setSearchValue(value) {
      this.searchPrefix = value

      const combo = this.$refs.recipientInput
      if (combo) {
        if (typeof combo.internalSearch !== "undefined") {
          combo.internalSearch = value
        }
        if (combo.$refs && combo.$refs.input && combo.$refs.input.value !== value) {
          combo.$refs.input.value = value
        }
      }
    },
    handleInvalidRecipient(invalidRecipient, oldRecipients) {
      this.validationError = "Enter a valid email address"
      this.isResettingRecipients = true
      this.selectedRecipients = [...oldRecipients]
      this.$nextTick(() => {
        this.isResettingRecipients = false
        this.setSearchValue(invalidRecipient)
        this.$nextTick(() => this.focusInput())
      })
    }
  },
  watch: {
    searchPrefix(newValue, oldValue) {
      this.$store.dispatch("updateRecipientsListUsingPrefix", this.searchPrefix)
    },
    selectedRecipients(newRecipients, oldRecipients = []) {
      if (this.isResettingRecipients) {
        return
      }

      const additions = newRecipients.filter(recipient => !oldRecipients.includes(recipient))

      // Only validate if there's text in searchPrefix (user is typing)
      if (this.searchPrefix && this.searchPrefix.trim()) {
        const invalidRecipient = this.findInvalidRecipient(additions)

        if (invalidRecipient) {
          this.handleInvalidRecipient(invalidRecipient, oldRecipients)
          return
        }
      } else {
        // Validate additions even if searchPrefix is empty (user might have selected from dropdown or pressed enter)
        if (additions.length > 0) {
          const invalidRecipient = this.findInvalidRecipient(additions)
          if (invalidRecipient) {
            this.handleInvalidRecipient(invalidRecipient, oldRecipients)
            return
          }
        }
      }

      if (this.validationError) {
        this.validationError = ""
      }

      this.searchPrefix = ""
      this.$emit("selectionChanged", this.selectedRecipients)
    }
  },
  mounted() {
    this.previousParticipants.forEach((participant) => {
      this.selectedRecipients.push(participant)
    })

    this.$forceUpdate()
  },
  props: {
    allParticipants: {
      type: Array,
      required: true
    },
    previousParticipants: {
      type: Array,
      required: true
    },
    displayCCbuttons: {
      type: Boolean,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    tabindex: {
      type: [String, Number],
      default: "2"
    }
  },
  computed: {
    hasValidationError() {
      return this.validationError !== ""
    },
    validationMessages() {
      return this.hasValidationError ? [this.validationError] : []
    }
  },
}
</script>

<style scoped>  
::v-deep .v-select__selections {
  row-gap: 5px;
  margin-bottom: 5px;
}
</style>
